<script lang="ts">
/**
 * List row for an item, used across dashboard, index, and container detail.
 *
 * Displays thumbnail, name, container path, and optional tags. Three
 * variants control what appears in each context:
 *
 * - `index`   — full display: tags on mobile below, tags + type on desktop right
 * - `compact` — no right-side content on desktop (dashboard recent items)
 * - `nested`  — hides container path (inside a container's child list)
 *
 * @example
 * ```vue
 * <ListItemRow :item="item" variant="index" />
 * <ListItemRow :item="item" variant="compact" />
 * ```
 */
export type ListItemRowVariant = "index" | "compact" | "nested"

export interface ListItemRowProps {
  /** Item data from the backend. */
  item: ItemSummary
  /** Display variant. @defaultValue `"index"` */
  variant?: ListItemRowVariant
}
</script>

<script setup lang="ts">
import { SbListItem, SbListSection, SbListLabel } from "@unio/saltbox-ui"

import TagBadge from "@/components/TagBadge.vue"
import { itemPath } from "@/routes"
import type { ItemSummary } from "@/types/models"

withDefaults(defineProps<ListItemRowProps>(), {
  variant: "index",
})

const maxTags = 3
</script>

<template>
  <SbListItem clickable :to="itemPath(item.id)">
    <SbListSection avatar>
      <UAvatar
        :src="item.thumb_url ?? undefined"
        :alt="item.name"
        size="lg"
        :ui="{ image: 'object-cover' }"
      />
    </SbListSection>

    <SbListSection>
      <div class="flex items-center gap-2">
        <SbListLabel :lines="1">{{ item.name }}</SbListLabel>
        <UBadge v-if="item.archived_at" color="warning" variant="subtle" size="sm">Archived</UBadge>
      </div>
      <SbListLabel
        v-if="(item.location_path || item.parent_name) && variant !== 'nested'"
        caption
        :lines="1"
      >
        {{ item.location_path || item.parent_name }}
      </SbListLabel>
      <!-- Tags: mobile only (below subtitle), not shown in compact variant -->
      <div
        v-if="item.tags.length && variant !== 'compact'"
        class="mt-1 flex flex-wrap items-center gap-1 lg:hidden"
      >
        <TagBadge
          v-for="tag in item.tags.slice(0, maxTags)"
          :key="tag.id"
          :tag="tag"
          :linked="false"
        />
        <span v-if="item.tags.length > maxTags" class="text-muted text-xs">
          +{{ item.tags.length - maxTags }}
        </span>
      </div>
    </SbListSection>

    <!-- Right side: desktop only — tags · item type per wireframe -->
    <SbListSection v-if="variant === 'index'" side class="hidden lg:flex">
      <div class="flex items-center gap-2">
        <template v-if="item.tags.length">
          <TagBadge
            v-for="tag in item.tags.slice(0, maxTags)"
            :key="tag.id"
            :tag="tag"
            :linked="false"
          />
          <span v-if="item.tags.length > maxTags" class="text-muted text-xs">
            +{{ item.tags.length - maxTags }}
          </span>
        </template>
        <span v-if="item.tags.length && item.item_type_name" class="text-muted text-xs">·</span>
        <SbListLabel v-if="item.item_type_name" caption>{{ item.item_type_name }}</SbListLabel>
      </div>
    </SbListSection>
  </SbListItem>
</template>
